import React, { useEffect, useState, useMemo } from "react";
import { Container, Card, Tabs, Tab, Box, Button, Grid, Typography, Divider, useTheme, Stack, Avatar } from "@mui/material";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useHistory } from "react-router-dom";

// Componentes
import CustomBreadcrumbs from "../../../components/breadcrumbs";
import QUDProgress from "../../../components/progress";
import PlansDataTable from "./plans/PlansDataTable"; 
import SubscribersList from "./subscriptions/SubscribersList"; 

// Controladores
import { getPlans, deletePlan } from "../../../controllers/company/subscriptions/PlansController";
import { getSubscriptions, cancelSubscription, getSubscriptionStatistics } from "../../../controllers/company/subscriptions/SubscriptionsController";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsSpin, faDollarSign, faUsers, faUserPlus, faPlus } from "@fortawesome/free-solid-svg-icons";
import ResponseComponent from "../../../components/responseComponent";

export default function SubscriptionsIndex() {
    const history = useHistory();
    const [tabValue, setTabValue] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    
    // Datos
    const [plans, setPlans] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [statistics, setStatistics] = useState(null);

    // Response State
    const [response, setResponse] = useState({ show: false, success: false, title: '', message: '' });

    const theme = useTheme();

    // Carga inicial
    useEffect(() => {
        window.scrollTo(0, 0);
        loadData();
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        const [plansRes, subsRes, statsRes] = await Promise.all([
            getPlans(),
            getSubscriptions(),
            getSubscriptionStatistics()
        ]);

        if (plansRes.success) setPlans(plansRes.data || []);
        if (subsRes.success) setSubscriptions(subsRes.data || []);
        if (statsRes.success) setStatistics(statsRes.data || null);

        setIsLoading(false);
    };

    const handleChangeTab = (event, newValue) => {
        setTabValue(newValue);
    };

    // --- MANEJADORES ---

    const handleDeletePlan = async (id) => {
        if(!window.confirm("¿Seguro que deseas eliminar este plan?")) return;

        setIsLoading(true);
        const res = await deletePlan(id);
        if (res.success) {
            setPlans(prev => prev.filter(p => p.id !== id));
            setResponse({ show: true, success: true, title: 'Operación Exitosa', message: 'Plan eliminado correctamente' });
        } else {
            setResponse({ show: true, success: false, title: 'Operación Fallida', message: 'No se pudo eliminar el plan. Verifica que no tenga suscriptores activos.' });
        }
        setIsLoading(false);
    };

    const handleCancelSubscription = async (id) => {
        if(!window.confirm("¿Cancelar esta suscripción? El alumno perderá el acceso al finalizar el periodo.")) return;
        setIsLoading(true);
        const res = await cancelSubscription(id);
        if(res.success) {
            setSubscriptions(prev => prev.map(sub => 
                sub.id === id ? { ...sub, status: 'canceled' } : sub
            ));
        } else {
             setResponse({ show: true, success: false, title: 'Error', message: res.message });
        }
        setIsLoading(false);
    };

    // Estilo base de tarjeta
    const cardStyle = { elevation: 0, sx: { border: '1px solid #e0e0e0', borderRadius: 3, p: { xs: 2, sm: 2.5 }, bgcolor: '#ffffff' } };

    return (
        <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 }, mb: 8 }}>
            <CustomBreadcrumbs returnTo='/dashboard' crumbs={[{ name: 'Suscripciones', link: '/subscriptions' }]} />
            
            {/* --- CABECERA PRINCIPAL --- */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mt: 3, mb: 4, gap: 2 }}>
                <Box>
                    <Typography variant="h4" fontWeight={800} sx={{ color: '#222', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                        Panel de Suscripciones
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                        Administra planes, servicios recurrentes y controla la actividad de tus alumnos.
                    </Typography>
                </Box>
                
                <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
                   <Button
                        fullWidth
                        variant='contained'
                        color="primary"
                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={() => history.push('/subscriptions/plans/create')}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, py: 1 }}
                    >
                        Nuevo Plan
                    </Button>
                </Box>
            </Box>

            {/* --- CABECERA DE ESTADÍSTICAS (KPIs) --- */}
            {statistics && (
                <Grid container spacing={3} mb={4}>
                    
                    {/* KPI 1: Ingresos de la Semana */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card {...cardStyle}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#e8f5e9', color: '#2e7d32', borderRadius: 2 }}>
                                    <FontAwesomeIcon icon={faDollarSign} size="sm" />
                                </Avatar>
                                <Box flexGrow={1} overflow="hidden">
                                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Ingresos (7 días)</Typography>
                                    <Typography variant="h6" noWrap sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>
                                        ${parseFloat(statistics.last_week.revenue).toFixed(2)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>

                    {/* KPI 2: Nuevos Inscritos (Semana) */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card {...cardStyle}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#e3f2fd', color: '#1976d2', borderRadius: 2 }}>
                                    <FontAwesomeIcon icon={faUserPlus} size="sm" />
                                </Avatar>
                                <Box flexGrow={1} overflow="hidden">
                                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Nuevos (7 días)</Typography>
                                    <Typography variant="h6" noWrap sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>
                                         {parseInt(statistics.last_week.count)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>

                    {/* KPI 3: Ingresos (Año) */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card {...cardStyle}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#f3e5f5', color: '#9c27b0', borderRadius: 2 }}>
                                    <FontAwesomeIcon icon={faDollarSign} size="sm" />
                                </Avatar>
                                <Box flexGrow={1} overflow="hidden">
                                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Ingresos (Año)</Typography>
                                    <Typography variant="h6" noWrap sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>
                                         ${parseFloat(statistics.last_year.revenue).toFixed(2)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>

                     {/* KPI 4: Total Inscritos (Año) */}
                     <Grid item xs={12} sm={6} md={3}>
                        <Card {...cardStyle}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#fff3e0', color: '#ed6c02', borderRadius: 2 }}>
                                    <FontAwesomeIcon icon={faUsers} size="sm" />
                                </Avatar>
                                <Box flexGrow={1} overflow="hidden">
                                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Alumnos (Año)</Typography>
                                    <Typography variant="h6" noWrap sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>
                                          {parseInt(statistics.last_year.count)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>
                </Grid>
            )}

            <Box>
                {/* --- PESTAÑAS --- */}
                <Box sx={{ borderBottom: 1, borderColor: '#e0e0e0', mb: 3 }}>
                    <Tabs value={tabValue} onChange={handleChangeTab} indicatorColor="primary">
                        <Tab label={<Typography fontWeight="bold" variant="body2" textTransform="none">Planes y Servicios</Typography>} />
                        <Tab label={<Typography fontWeight="bold" variant="body2" textTransform="none">Todos los Suscriptores</Typography>} />
                    </Tabs>
                </Box>

                {/* --- CONTENIDO TAB 1: PLANES --- */}
                <Box role="tabpanel" hidden={tabValue !== 0}>
                    {tabValue === 0 && (
                        <PlansDataTable 
                            plans={plans} 
                            onDeletePlan={handleDeletePlan} 
                        />
                    )}
                </Box>

                {/* --- CONTENIDO TAB 2: SUSCRIPTORES --- */}
                <Box role="tabpanel" hidden={tabValue !== 1}>
                    {tabValue === 1 && (
                        <SubscribersList 
                            subscriptions={subscriptions}
                            onCancel={handleCancelSubscription}
                        />
                    )}
                </Box>
            </Box>

            <QUDProgress open={isLoading} />
            <ResponseComponent
                title={response.title}
                show={response.show}
                success={response.success}
                message={response.message}
                actionText={'Finalizar'}
                onClick={() => setResponse({ ...response, show: false })}
            />
        </Container>
    );
}